import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setSubmitError('');
    
    try {
      const response = await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email
      });
      
      console.log('Lead submitted:', response.data);
      navigate('/opportunity');
    } catch (error) {
      console.error('Error submitting lead:', error);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Blue Mountain Background - positioned to show hikers and blue mountains */}
      <div 
        className="absolute inset-0 bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1682985045797-0b781d0c0f7a?w=1920&q=80')`,
          backgroundPosition: 'center 30%',
        }}
      />
      
      {/* 50% overlay for text to POP */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Subtle Logo */}
        <header className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="inline-flex items-center gap-2">
              <span className="text-white/90 text-xl font-light tracking-wide">
                CRN
              </span>
              <span className="text-white/50 text-sm font-light">
                Cellular Recovery Network
              </span>
            </div>
          </div>
        </header>
        
        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-6xl w-full">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left - Message */}
              <div className="text-center lg:text-left">
                <h1 
                  className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
                  style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.5)' }}
                >
                  Your Recovery Creates<br />
                  a Path for Others.
                </h1>
                
                <p 
                  className="text-xl md:text-2xl text-white leading-relaxed max-w-lg mx-auto lg:mx-0"
                  style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.6)' }}
                >
                  Guide others toward deeper, physical recovery by addressing what's often missed: the cellular repair required for long-term health, longevity, and a better quality of life.
                </p>
              </div>
              
              {/* Right - Form - White card that pops */}
              <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="bg-white/50 backdrop-blur-md rounded-2xl p-8 shadow-2xl border border-stone-200">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-black mb-2">
                      What If Your Recovery Was Just the Beginning of Your Journey?
                    </h2>
                    <p className="text-black font-semibold mb-3">
                      Natural Born Leaders Wanted.
                    </p>
                    <p className="text-black text-sm">
                      Download Your Free PDF And Learn More On The Next Page
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        type="text"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={`h-12 rounded-xl bg-stone-50 border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 text-stone-800 placeholder:text-stone-400 ${errors.name ? 'border-amber-500' : ''}`}
                      />
                      {errors.name && (
                        <p className="text-amber-600 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    
                    <div>
                      <Input
                        type="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={`h-12 rounded-xl bg-stone-50 border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 text-stone-800 placeholder:text-stone-400 ${errors.email ? 'border-amber-500' : ''}`}
                      />
                      {errors.email && (
                        <p className="text-amber-600 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    
                    {submitError && (
                      <p className="text-amber-600 text-sm text-center">{submitError}</p>
                    )}
                    
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold rounded-xl shadow-lg shadow-amber-600/30 hover:shadow-xl hover:shadow-amber-600/40 transition-all"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Learn More
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                  
                  <p className="text-xs text-black text-center mt-4">
                    We do not use 3rd party email - your email is safe and secure with us.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
        
        {/* Minimal Footer */}
        <footer className="py-4 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Cellular Recovery Network
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
